import { createSelector } from "reselect";
import { get } from "lodash";
import { DistributionList, Case, CaseFollow } from "typings";
import { RootState } from "../reducers";
import { mapCaseToDistributionLists } from "./utils";
import { selectSelectedMappedFollows } from "./distributionLists";

export const selectDrawerUsers = (state: RootState) => state.drawer.users.data;

export const selectDrawerDistributionLists = (state: RootState) =>
  state.drawer.distributionLists.data;

export const selectDrawerDistributionListsIncluded = (state: RootState) =>
  state.drawer.distributionLists.included;

export const selectDrawerDistributionListIncludedCaseFollows = createSelector(
  selectDrawerDistributionListsIncluded,
  included => included.filter(related => related.type === "caseFollows"),
);

export const selectDrawerDistributionListIncludedCases = createSelector(
  selectDrawerDistributionListsIncluded,
  included => included.filter(related => related.type === "cases"),
);

export const selectFilteredDrawerUsers = createSelector(
  selectDrawerUsers,
  selectSelectedMappedFollows,
  (drawerUsers, follows) =>
    drawerUsers.filter(
      drawerUser => !follows.some(follow => follow.userId === drawerUser.id),
    ),
);

export const selectMappedDrawerDistributionLists = createSelector(
  selectDrawerDistributionLists,
  selectDrawerDistributionListsIncluded,
  mapCaseToDistributionLists,
);

const findCase = (
  distributionList: DistributionList,
  includedCases: Case[],
) => {
  const caseId = get(distributionList.relationships.case, "data.id");
  return includedCases.find(includedCase => includedCase.id === caseId);
};

const mapCaseFollows = (
  distributionList: DistributionList,
  includedCaseFollows: CaseFollow[],
) => {
  const distributionListCaseFollows = get(
    distributionList.relationships.members,
    "data",
    [],
  );
  return includedCaseFollows.filter(includedCaseFollow =>
    distributionListCaseFollows.some(
      distributionListCaseFollow =>
        distributionListCaseFollow.id === includedCaseFollow.id,
    ),
  );
};

export const selectFilteredDrawerDistributionLists = createSelector(
  selectDrawerDistributionLists,
  selectDrawerDistributionListIncludedCases,
  selectDrawerDistributionListIncludedCaseFollows,
  selectSelectedMappedFollows,
  (distributionLists, includedCases, includedCaseFollows, follows) =>
    distributionLists
      .map(distributionList => {
        /**
         * finding related case using related case id
         */
        const relatedCase = findCase(distributionList, includedCases);
        /**
         * filtering included case follows and matching them to the current distribution list
         */
        const caseFollows = mapCaseFollows(
          distributionList,
          includedCaseFollows,
        );
        /**
         * filtering current distribution list case follows against the selected follows
         */
        const filteredCaseFollows = caseFollows
          .filter(
            caseFollow =>
              !follows.some(follow => {
                if (follow.userId) {
                  const userId = get(
                    caseFollow.relationships.user,
                    "data.id",
                    "",
                  );
                  return follow.userId === userId;
                }
                return follow.email === caseFollow.attributes.email;
              }),
          )
          .map(caseFollow => {
            const userId = get(caseFollow.relationships.user, "data.id");
            return {
              caseFollowId: caseFollow.id,
              externalUserEmail: !userId
                ? caseFollow.attributes.email
                : undefined,
              userId,
              ...caseFollow.attributes,
            };
          });

        if (distributionList && relatedCase && filteredCaseFollows) {
          return {
            distributinListId: distributionList.id,
            caseId: relatedCase.id,
            follows: filteredCaseFollows,
            ...distributionList.attributes,
            ...relatedCase.attributes,
          };
        }
      })
      .filter(distributionList => distributionList)
      .filter(
        distributionList =>
          distributionList && distributionList.follows.length > 0,
      ),
);
